import './styles/index.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App.js';
import { BootFailure } from './BootFailure.js';
import { bootstrapUserAuth } from './lib/auth/user-session.js';
import { userSessionStore } from './lib/auth/user-session-store.js';
import { bootCatalog } from './lib/i18n/catalog-store.js';
import { installZodJitless } from './lib/forms/zod-jitless.js';
import { CORE_NAMESPACES } from './lib/i18n/namespaces.js';
import { CatalogProvider } from './providers/CatalogProvider.js';

/**
 * Ядро каталога локали загружается ДО первого рендера: своих текстов у SPA нет
 * (§0 CLAUDE.md), и без каталога рисовать нечего. Обёрнуто в async-функцию с try/catch,
 * а не в top-level await: именно catch превращает неудачную загрузку каталога в аварийный
 * экран вместо белой страницы.
 *
 * Восстановление сессии идёт ПАРАЛЛЕЛЬНО с каталогом, а не после: это два независимых
 * запроса, и последовательный запуск удвоил бы время до первого рендера.
 *
 * ⚠️ Их отказы обрабатываются ПО-РАЗНОМУ, поэтому Promise.all тут не годится. Без каталога
 * приложение нарисовать нельзя — это аварийный экран. А неудачное восстановление сессии
 * это штатный случай (гость, протухший refresh), приложение обязано открыться лендингом.
 * Поэтому у бутстрапа сессии свой catch, гасящий отказ в статус `guest`.
 */
/**
 * ⚠️ ДО первой валидации, то есть до любого рендера: zod выполняет пробу `new Function`
 * один раз и кэширует результат. Настроить его позже — значит уже получить нарушение CSP
 * в консоли. Подробности — в самом модуле.
 */
installZodJitless();

async function start(): Promise<void> {
  const container = document.getElementById('root');
  if (container === null) throw new Error('#root not found');
  const root = createRoot(container);

  const session = bootstrapUserAuth().catch((err: unknown) => {
    console.error('session bootstrap failed', err);
    /**
     * ⚠️ Гасить отказ в `guest` ОБЯЗАТЕЛЬНО, а не просто логировать.
     *
     * Стор стартует в состоянии `loading`, и guard'ы в этом состоянии показывают
     * заглушку, а не содержимое. Неожиданное исключение мимо внутренних обработчиков
     * bootstrapUserAuth оставило бы стор в `loading` навсегда — пользователь увидел бы
     * вечный спиннер вместо лендинга, и никакая повторная навигация это не вылечила бы.
     */
    userSessionStore.clear();
  });

  try {
    /**
     * ⚠️ Грузится ЯДРО каталога, а не весь каталог. Остальное страницы догружают сами,
     * в ленивом импорте маршрута (lib/i18n/namespaces.ts). До этой правки перед первым
     * кадром уходило одиннадцать запросов за namespace, среди них `admin`.
     */
    await bootCatalog(CORE_NAMESPACES);
    await session;
    root.render(
      <StrictMode>
        <CatalogProvider>
          <App />
        </CatalogProvider>
      </StrictMode>,
    );
  } catch (err: unknown) {
    console.error('i18n boot failed', err);
    root.render(<BootFailure />);
  }
}

void start();
